import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import { logout } from "@/store/authSlice";
import { useActiveModule } from "@/hooks/useActiveModule";
import { moduleAdminPath } from "@/config/moduleDefaults";
import { resolveLogoutLoginPath } from "@/routes/ModuleScopeGuard";
import { UserAvatar } from "@/components/user/UserAvatar";
import { readUserAvatar } from "@/utils/userAvatarStorage";
import { cn } from "@/utils/cn";

import type { ErpModule } from "@/api/platform.api";

interface ProfileMenuProps {
  variant?: "default" | "header";
}

function resolveProfilePath(
  masterAdmin?: boolean,
  activeModule?: ErpModule | null,
  entryModule?: ErpModule | null,
): string {
  if (masterAdmin) return "/master/profile";
  const module = activeModule ?? entryModule ?? "POS";
  return moduleAdminPath(module, "profile");
}

export function ProfileMenu({ variant = "default" }: ProfileMenuProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((s) => s.auth.user);
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const activeModule = useActiveModule();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    user ? readUserAvatar(user.userUuid) : null,
  );

  useEffect(() => {
    if (!user) {
      setAvatarUrl(null);
      return;
    }
    setAvatarUrl(readUserAvatar(user.userUuid));
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const syncAvatar = (event: Event) => {
      const detail = (event as CustomEvent<{ userUuid?: string }>).detail;
      if (!detail?.userUuid || detail.userUuid === user.userUuid) {
        setAvatarUrl(readUserAvatar(user.userUuid));
      }
    };
    window.addEventListener('user-avatar-updated', syncAvatar);
    return () => window.removeEventListener('user-avatar-updated', syncAvatar);
  }, [user]);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleLogout = async () => {
    const loginPath = resolveLogoutLoginPath({
      masterAdmin: user?.masterAdmin,
      activeModule,
      entryModule,
      pathname: location.pathname,
    });
    await dispatch(logout());
    navigate(loginPath, { replace: true });
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        className={cn(
          variant === "header"
            ? "workspace-header-profile-btn flex items-center justify-center overflow-hidden p-0"
            : "workspace-header-icon-btn",
        )}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Account menu"
      >
        {user && variant === "header" ? (
          <UserAvatar
            firstName={user.firstName}
            lastName={user.lastName}
            imageUrl={avatarUrl}
            size="sm"
            className="ring-0"
          />
        ) : (
          <PersonIcon fontSize={variant === "header" ? "medium" : "small"} />
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-[100] mt-2 w-56 rounded-xl border border-slate-200 bg-white py-1 shadow-panel dark:border-slate-700 dark:bg-slate-900"
          role="menu"
        >
          <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-800">
            <div className="flex items-center gap-2">
              {user && (
                <UserAvatar
                  firstName={user.firstName}
                  lastName={user.lastName}
                  imageUrl={avatarUrl}
                  size="sm"
                  className="ring-0"
                />
              )}
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="truncate text-xs text-slate-500">{user?.email}</p>
              </div>
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            className={cn(
              "flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-900 hover:bg-slate-50 dark:text-white dark:hover:bg-slate-800",
            )}
            onClick={() => {
              setOpen(false);
              navigate(resolveProfilePath(user?.masterAdmin, activeModule, entryModule));
            }}
          >
            <PersonIcon fontSize="small" />
            My profile
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
            onClick={() => void handleLogout()}
          >
            <LogoutIcon fontSize="small" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
